import { useMutation } from '@tanstack/react-query';

import { deleteServicePart, postServiceParts } from 'queries';
import { JsonData, JsonDataItem } from 'types';
import { parseErrors, Toast } from 'helpers';

export const useServicePartsUpdate = () => {
  const handleMutationError = (error: JsonDataItem) => {
    Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
  };

  const {
    mutate: deletePart,
    isLoading: isDeleting,
    isError: isDeleteError,
  } = useMutation(deleteServicePart, {
    onError: handleMutationError,
  });

  const {
    mutate: addParts,
    isLoading: isAdding,
    isError: isAddError,
  } = useMutation(postServiceParts, {
    onError: handleMutationError,
  });

  const compareAndUpdate = (newParts: JsonData, oldParts: JsonData, serviceId: number) => {
    const oldPartsMap = new Map<number, JsonDataItem>(oldParts.map((part: JsonDataItem) => [part.ID, part]));
    const partsToAdd = newParts?.filter((part) => !oldPartsMap.has(parseInt(part.id)));
    const partsToRemove = Array.from(oldPartsMap.keys()).filter(
      (id) => !newParts?.some((part) => Number(part.id) === id),
    );

    if (partsToRemove.length) {
      partsToRemove.forEach((partId) => deletePart({ serviceId, partId }));
    }

    if (partsToAdd?.length) {
      addParts({
        serviceId,
        parts: partsToAdd.map((part) => {
          return {
            inventory_id: part.id.toString().includes('temp') ? undefined : part.id, // custom (new) parts has temp id
            description: part.description,
            price: part.price,
            qty: part.qty,
          };
        }),
      });
    }
  };

  const isLoading = isDeleting || isAdding;
  const isError = isDeleteError || isAddError;

  const updateServiceParts = (props: JsonDataItem) => {
    const { newParts, initialParts, serviceId } = props;

    return compareAndUpdate(newParts, initialParts, serviceId);
  };

  return { updateServiceParts, isLoading, isError };
};
